import { Group } from 'src/design/atoms/Group';
import { Loader } from 'src/design/atoms/Loader';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Accordion } from 'src/design/molecules/Accordion';
import { Table } from 'src/design/molecules/Table';
import { TextInput } from 'src/design/atoms/TextInput';
import React, { useMemo, useState } from 'react';
import LinesEllipsis from 'react-lines-ellipsis';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { OrganizationRoleDto } from 'src/api/FiretrolApiClient';
import { useRoles } from 'src/api/queries/organization/useRoles';
import { useDeleteRole } from 'src/api/queries/organization/useRoleMutations';
import { useDialogs } from 'src/hooks/useDialogs';
import { DeleteActionIcon } from 'src/design/molecules/DeleteActionIcon';
import { EditActionIcon } from 'src/design/atoms/EditActionIcon';
import { CreateButton } from 'src/design/atoms/CreateButton';
import { AvatarGroup } from 'src/design/molecules/AvatarGroup';
import { TagBadgeList } from 'src/design/molecules/TagBadgeList';
import { Header } from 'src/design/organisms/Header';

export const Roles: React.FC = () => {
  const { data, isLoading: loading } = useRoles();
  const roles = React.useMemo(() => data ?? [], [data]);
  const deleteRole = useDeleteRole();
  const { setScopeInfoOpen } = useDialogs();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const openOffices =
    searchParams.get('office')?.split(',').filter(Boolean) ?? [];

  // Carry the current list URL (incl. ?office=) so edit/create pages can return here with expansion intact.
  const listOrigin = { from: `${location.pathname}${location.search}` };

  const setOpenOffices = (offices: string[]) =>
    setSearchParams(offices.length ? { office: offices.join(',') } : {}, {
      replace: true,
    });
  const [searchTerm, setSearchTerm] = useState('');

  const groupedRoles = useMemo(() => {
    const groups: Record<string, OrganizationRoleDto[]> = {};
    roles.forEach((role) => {
      const office = role.office ? role.office : 'Global';
      if (!groups[office]) {
        groups[office] = [];
      }
      if (
        office.toLowerCase().includes(searchTerm.toLowerCase()) ||
        role.name.toLowerCase().includes(searchTerm.toLowerCase())
      ) {
        groups[office].push(role);
      }
    });
    return Object.fromEntries(
      Object.entries(groups).filter(([_, roles]) => roles.length > 0),
    );
  }, [roles, searchTerm]);

  return (
    <>
      <Header
        title="Roles"
        helpText="Roles determine access to features through the use of pre-defined scopes. Scopes control what resources a user with a role can read, write, and/or manage."
        breadcrumbProps={{
          items: [
            {
              text: 'Settings',
            },
            {
              text: 'Roles',
            },
          ],
        }}
      />

      <Stack>
        <Text>
          A role provides access to pre-defined pages, data, menus, and features
          through the use of scopes. Scopes control what resources a user with a
          role can read, write, and/or manage. Click{' '}
          <a onClick={() => setScopeInfoOpen(true)}>
            <b>HERE</b>
          </a>{' '}
          for more information on scopes.
        </Text>

        {loading ? (
          <div className="text-center">
            <Loader />
          </div>
        ) : (
          <>
            <Group justify="space-between">
              <CreateButton
                onClick={() =>
                  navigate('/settings/roles/create', { state: listOrigin })
                }
              >
                Create Role
              </CreateButton>
              <TextInput
                placeholder="Search by office or role name"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.currentTarget.value)}
              />
            </Group>
            <Accordion multiple value={openOffices} onChange={setOpenOffices}>
              {Object.entries(groupedRoles).map(([office, officeRoles]) => (
                <Accordion.Item key={office} value={office}>
                  <Accordion.Control>
                    <Text c="black" fw={500}>
                      {office} ({officeRoles.length})
                    </Text>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Table style={{ tableLayout: 'fixed' }}>
                      <colgroup>
                        <col style={{ width: '110px' }} />
                        <col style={{ width: '160px' }} />
                        <col style={{ width: '30%' }} />
                        <col style={{ width: '30%' }} />
                        <col style={{ width: '180px' }} />
                      </colgroup>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Actions</Table.Th>
                          <Table.Th>Role</Table.Th>
                          <Table.Th>Description</Table.Th>
                          <Table.Th>Scopes</Table.Th>
                          <Table.Th>Accounts</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {officeRoles.map((role) => (
                          <Table.Tr key={role.id}>
                            <Table.Td>
                              <Group>
                                <EditActionIcon
                                  onClick={() =>
                                    navigate(
                                      `/settings/roles/edit/${role.id}`,
                                      {
                                        state: listOrigin,
                                      },
                                    )
                                  }
                                />
                                <DeleteActionIcon
                                  requiredConfirmation={true}
                                  onDelete={() => deleteRole.mutate(role)}
                                  loading={deleteRole.isPending}
                                />
                              </Group>
                            </Table.Td>
                            <Table.Td>{role.name}</Table.Td>
                            <Table.Td>
                              <LinesEllipsis
                                text={role.description}
                                maxLine={2}
                                ellipsis="..."
                                trimRight
                                basedOn="letters"
                              />
                            </Table.Td>
                            <Table.Td>
                              <TagBadgeList tags={role.scopes} maxItems={2} />
                            </Table.Td>
                            <Table.Td>
                              {role.users.length > 0 ? (
                                <Group>
                                  <AvatarGroup
                                    items={role.users.map((user) => ({
                                      key: String(user.id),
                                      name: `${user.firstName} ${user.lastName}`,
                                      src: '', // TODO: Add avatar,
                                    }))}
                                    maxItems={4}
                                  />
                                </Group>
                              ) : (
                                <Text>No accounts with role</Text>
                              )}
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </Accordion.Panel>
                </Accordion.Item>
              ))}
            </Accordion>
          </>
        )}
      </Stack>
    </>
  );
};
